import { cleanBrandKitWebsite } from "../lib/brandKitWebsite";
import {
  collection,
  deleteField,
  doc,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, getStorage, ref, uploadBytes } from "firebase/storage";
import { MessageSquareText, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  BrandKitModal,
  defaultColorSettings,
  defaultFontSettings,
} from "../components/BrandKitModal";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import {
  brandColorPositionLabel,
  getBrandColorList,
} from "../lib/brandColors";
import { cn } from "../lib/utils";
import {
  commitBrandKitAssets,
  deleteBrandKit,
  getBrandKitsForUser,
  refreshBrandKitColorSystem,
} from "../lib/brandKitUtils";
import { BrandKit } from "../models/BrandKit";
import { ImageOverlay } from "../models/Post";
import { db } from "../services/Firebase";
import { logoSurfaceClass, useLogoTone } from "../lib/logoTone";

const BrandKitCard = ({
  kit,
  onEdit,
  onDelete,
}: {
  kit: BrandKit;
  onEdit: (kit: BrandKit) => void;
  onDelete: (kitId: string) => void;
}) => {
  const { url: visualAssetUrl, loading: logoLoading } = useFirebaseUrl(kit.logoPath);
  const logoTone = useLogoTone(kit.logoPath ? visualAssetUrl : null);
  const palette = getBrandColorList(kit.colors);
  const lead = palette[0];
  const headingFont = kit.fonts?.primaryFont;
  const bodyFont = kit.fonts?.bodyFont;
  const fontNames = [
    kit.fonts?.primaryFont,
    kit.fonts?.secondaryFont,
    kit.fonts?.bodyFont,
  ].filter(Boolean) as string[];
  const monogram = (kit.brandName || kit.name || "B")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(kit)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(kit);
        }
      }}
      aria-label={`Edit ${kit.name}`}
      title={`Edit ${kit.name}`}
      className="group cursor-pointer overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {/* Brand band — the kit's lead color is the card's identity */}
      <div
        className={cn("relative h-24 border-b", !lead && "bg-muted")}
        style={lead ? { backgroundColor: lead } : undefined}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(kit.id);
          }}
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/85 text-muted-foreground shadow-sm transition-colors hover:bg-white hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Delete ${kit.name}`}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <div className={`absolute -bottom-7 left-5 flex h-14 w-14 items-center justify-center rounded-full shadow-md ring-1 ring-black/5 ${logoSurfaceClass(logoTone)}`}>
          {kit.logoPath && visualAssetUrl ? (
            <img
              src={visualAssetUrl}
              alt={`${kit.name} logo`}
              className="h-11 w-11 rounded-full object-contain"
            />
          ) : kit.logoPath && logoLoading ? (
            <div className="h-11 w-11 rounded-full bg-gray-100 animate-pulse" />
          ) : (
            <span
              className="text-lg font-semibold text-muted-foreground"
              style={headingFont ? { fontFamily: headingFont } : undefined}
            >
              {monogram}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3 px-5 pb-5 pt-9">
        <div>
          <h3
            className="truncate text-lg font-semibold leading-tight"
            style={headingFont ? { fontFamily: headingFont } : undefined}
          >
            {kit.name}
          </h3>
          {kit.tagline ? (
            <p
              className="mt-0.5 truncate text-sm text-muted-foreground"
              style={bodyFont ? { fontFamily: bodyFont } : undefined}
            >
              {kit.tagline}
            </p>
          ) : null}
        </div>

        {palette.length > 0 ? (
          <div className="flex items-center -space-x-1.5">
            {palette.map((color, index) => (
              <div
                key={`${color}-${index}`}
                className="h-6 w-6 rounded-full border border-black/10 ring-2 ring-card"
                style={{
                  backgroundColor: color,
                  zIndex: palette.length - index,
                }}
                title={`${brandColorPositionLabel(index)}: ${color}`}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No colors yet — open the kit to add a palette
          </p>
        )}

        {(kit.brandVoiceTone || fontNames.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {kit.brandVoiceTone && (
              <span
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                title={`Brand voice tone — generated copy sounds ${kit.brandVoiceTone}`}
              >
                <MessageSquareText className="h-3 w-3" />
                <span className="capitalize">{kit.brandVoiceTone}</span> voice
              </span>
            )}
            {fontNames.length > 0 && (
              <span className="truncate">{fontNames.join(" · ")}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function BrandKitsPage() {
  const { currentUser, activeWorkspaceId } = useAuth();
  const { toast } = useToast();
  const { currentPlan } = useStripe();
  const [userBrandKits, setUserBrandKits] = useState<BrandKit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBrandKitModalOpen, setIsBrandKitModalOpen] = useState(false);
  const [editingBrandKit, setEditingBrandKit] = useState<BrandKit | null>(null);
  const [isCreatingNewKit, setIsCreatingNewKit] = useState(false);
  const { navigateToUpgrade } = useUpgradePage();

  // Note: doesn't set isLoading(true) — it starts true for the initial
  // load, and refetches (after save/delete/color-system refresh) should
  // swap data in place instead of flashing the skeleton grid.
  const fetchUserBrandKits = useCallback(async () => {
    if (currentUser && activeWorkspaceId) {
      try {
        const kits = await getBrandKitsForUser(activeWorkspaceId);
        setUserBrandKits(kits);
      } catch (error) {
        console.error("Error fetching brand kits:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not load your brand kits.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [currentUser, activeWorkspaceId, toast]);

  useEffect(() => {
    fetchUserBrandKits();
  }, [fetchUserBrandKits]);
  // Get the brand kit limit based on the user's plan
  const getBrandKitLimit = useCallback((): number | null => {
    // If no plan or stripePriceId (free user), default to 1
    if (!currentPlan || !currentPlan.stripePriceId) {
      return 1;
    }

    // Safety check: if planDetail is missing despite having a stripePriceId,
    // we default to 1 (safe fallback) but log a warning.
    if (!currentPlan.planDetail) {
      console.warn(
        "Stripe plan ID present but plan details missing. Defaulting to 1 brand kit."
      );
      return 1;
    }

    const { brand_kits } = currentPlan.planDetail;

    // If brand_kits is undefined or null, it means unlimited
    if (brand_kits === undefined || brand_kits === null) {
      return null;
    }

    return brand_kits;
  }, [currentPlan]);

  const handleOpenBrandKitModal = useCallback(
    (kitToEdit?: BrandKit) => {
      // If editing, always allow
      if (kitToEdit) {
        setEditingBrandKit(kitToEdit);
        setIsBrandKitModalOpen(true);
        return;
      }
      if (!activeWorkspaceId) return;

      // Check brand kit limit for new kits
      const brandKitLimit = getBrandKitLimit();
      console.log("brandKitLimit", brandKitLimit);
      const hasReachedLimit =
        brandKitLimit !== null && userBrandKits.length >= brandKitLimit;

      if (hasReachedLimit) {
        // Don't redirect — the button is disabled and shows an inline upgrade prompt
        return;
      }

      // Allow creating new kit — pre-generate an ID so uploads work immediately
      const newKitId = doc(collection(db, "brand_kits")).id;
      const skeletonKit: BrandKit = {
        id: newKitId,
        userId: activeWorkspaceId,
        name: "",
        fonts: defaultFontSettings,
        colors: defaultColorSettings,
        primaryBrandElement: "auto",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      setEditingBrandKit(skeletonKit);
      setIsCreatingNewKit(true);
      setIsBrandKitModalOpen(true);
    },
    [activeWorkspaceId, getBrandKitLimit, userBrandKits.length]
  );

  const handleCloseBrandKitModal = useCallback(() => {
    setEditingBrandKit(null);
    setIsCreatingNewKit(false);
    setIsBrandKitModalOpen(false);
  }, []);

  const handleSaveBrandKit = async (
    finalKitData: Partial<BrandKit>,
    files: {
      visualAsset?: File;
      cover?: File;
      bRoll?: File[];
    }
  ) => {
    if (!currentUser || !activeWorkspaceId) {
      throw new Error("Please sign in again before saving.");
    }

    const storage = getStorage();
    const isEditing = !!editingBrandKit && !isCreatingNewKit;
    const kitId = editingBrandKit?.id || doc(collection(db, "brand_kits")).id;

    // Establish a complete base object, whether editing or creating
    const defaultNewKit: BrandKit = {
      id: kitId,
      userId: activeWorkspaceId,
      workspaceId: activeWorkspaceId,
      name: "",
      fonts: defaultFontSettings,
      colors: defaultColorSettings,
      logoPath: undefined,
      brandName: undefined,
      tagline: undefined,
      website: undefined,
      primarySocial: undefined,
      primaryBrandElement: "auto",
      bRollVideos: [],
      outroSettings: undefined,
      watermarkSettings: undefined,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const baseData = editingBrandKit || defaultNewKit;

    // Create the final data by merging the base with the changes from the modal
    // Note: finalKitData might exclude brandAssets to prevent overwriting during edit,
    // but if we merge with baseData (which has old brandAssets), we risk bringing them back.
    // We'll handle this in the update logic below.
    const finalData: BrandKit = {
      ...baseData,
      ...finalKitData,
      updatedAt: Timestamp.now(), // Always update the timestamp
    };
    if (typeof finalData.website === "string") {
      finalData.website = cleanBrandKitWebsite(finalData.website);
    }

    console.log("BrandKitsPage: handleSaveBrandKit started", { 
      isEditing, 
      finalKitDataHasAssets: 'brandAssets' in finalKitData,
      baseDataAssetsCount: baseData.brandAssets?.length
    });

    try {
      // Handle Visual Asset Upload/Deletion
      let newVisualAssetPath: string | undefined = undefined;
      if (files.visualAsset) {
        if (editingBrandKit?.logoPath) {
          try {
            await deleteObject(ref(storage, editingBrandKit.logoPath));
          } catch (e) {
            console.warn("Old visual asset not found for deletion:", e);
          }
        }

        // Use backend endpoint for upload with SVG conversion
        const idToken = await currentUser.getIdToken();
        const formData = new FormData();
        formData.append("file", files.visualAsset);
        formData.append("brandKitId", kitId);

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/brandkit/upload-logo`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to upload logo");
        }

        const result = await response.json();
        finalData.logoPath = result.path;
        newVisualAssetPath = result.path;

        if (result.converted) {
          console.log("Logo was automatically converted from SVG to PNG");
          toast({
            title: "SVG Converted",
            description:
              "Your logo was automatically converted to PNG format for compatibility.",
          });
        }
      }

      // After visual asset upload, update settings that use the asset path
      if (newVisualAssetPath) {
        // Update Outro
        if (finalData.outroSettings?.overlays) {
          const logoOverlay = finalData.outroSettings.overlays.find(
            (o) => o.id === "brandkit-outro-logo"
          );
          if (logoOverlay && logoOverlay.type === "image") {
            logoOverlay.filePath = newVisualAssetPath;
          }
        }
        // Update Watermark
        if (
          finalData.watermarkSettings?.id === "brandkit-watermark-logo" &&
          finalData.watermarkSettings.type === "image"
        ) {
          (finalData.watermarkSettings as ImageOverlay).filePath =
            newVisualAssetPath;
        }
      }

      // Handle B-Roll Upload/Deletion
      const initialBRollPaths = editingBrandKit?.bRollVideos || [];
      const finalBRollPathsFromModal = finalKitData.bRollVideos || [];

      const pathsToDelete = initialBRollPaths.filter(
        (p) => !finalBRollPathsFromModal.includes(p)
      );
      await Promise.all(
        pathsToDelete.map((p) =>
          deleteObject(ref(storage, p)).catch((e) =>
            console.warn(`Failed to delete B-roll ${p}`, e)
          )
        )
      );

      const uploadedBRollPaths: string[] = [];
      if (files.bRoll && files.bRoll.length > 0) {
        await Promise.all(
          files.bRoll.map(async (file, index) => {
            const ext = file.name.split(".").pop() || "mp4";
            const newPath = `brandKits/${
              activeWorkspaceId
            }/${kitId}/b-roll_${Date.now()}_${index}.${ext}`;
            await uploadBytes(ref(storage, newPath), file);
            uploadedBRollPaths.push(newPath);
          })
        );
      }
      finalData.bRollVideos = [
        ...finalBRollPathsFromModal,
        ...uploadedBRollPaths,
      ];

      // Use updateDoc for editing to avoid overwriting fields not in finalKitData (like brandAssets)
      // Use setDoc for new kits
      if (isEditing) {
        // IMPORTANT: When editing, we must NOT save brandAssets if they were excluded from finalKitData.
        // If finalKitData doesn't have brandAssets, but we merge with baseData (which does),
        // finalData WILL have the stale brandAssets. We must explicitly delete it from the payload.
        const updatePayload = { ...finalData };
        
        console.log("BrandKitsPage: isEditing=true. finalKitData has brandAssets?", 'brandAssets' in finalKitData);
        console.log("BrandKitsPage: baseData has brandAssets count:", baseData.brandAssets?.length);

        // If the modal deliberately excluded brandAssets (because it handled them separately),
        // we must ensure we don't write the stale ones from baseData back to Firestore.
        if (!('brandAssets' in finalKitData)) {
          console.log("BrandKitsPage: Removing brandAssets from updatePayload to prevent overwrite");
          delete (updatePayload as any).brandAssets;
        } else {
          console.log("BrandKitsPage: Keeping brandAssets in updatePayload (was included in finalKitData)");
        }

        // colorSystem is server-owned (derived + cached by the server). The
        // modal strips it, but the merge with baseData re-introduces the
        // snapshot from page load — writing it back would clobber anything
        // the server derived since (e.g. via Preview). Omitting the field
        // leaves the server's value untouched.
        delete (updatePayload as any).colorSystem;
        
        // Firestore updateDoc ignores undefined values, so fields the user
        // cleared would silently persist. Replace undefined with deleteField().
        for (const key of Object.keys(updatePayload)) {
          if ((updatePayload as any)[key] === undefined) {
            (updatePayload as any)[key] = deleteField();
          }
        }

        console.log("BrandKitsPage: Final updatePayload keys:", Object.keys(updatePayload));
        await updateDoc(doc(db, "brand_kits", kitId), updatePayload as any);
      } else {
        // For new kits: save the kit WITHOUT brandAssets first, then use
        // commitBrandKitAssets to add them via arrayUnion + enqueue processing.
        // Writing assets in BOTH setDoc and commitBrandKitAssets causes duplicates
        // due to Timestamp serialization differences between client/admin SDK.
        const assetsToCommit = finalData.brandAssets || [];
        const dataToSave = { ...finalData };
        delete dataToSave.brandAssets;
        await setDoc(doc(db, "brand_kits", kitId), dataToSave);

        // Commit assets separately so the server can validate ownership,
        // persist them, and enqueue processing. A failure must propagate: the
        // modal keeps the user's selections open and a retry is idempotent.
        if (assetsToCommit.length > 0) {
          const idToken = await currentUser.getIdToken();
          await commitBrandKitAssets(kitId, assetsToCommit, idToken);
        }
      }

      // Optionally, refetch brand kits
      await fetchUserBrandKits();

      // Re-derive the color usage system in the background, then refresh so
      // cards and the next editor open show the fresh system.
      currentUser
        .getIdToken()
        .then((token) => refreshBrandKitColorSystem(kitId, token))
        .then(() => fetchUserBrandKits())
        .catch((e) =>
          console.warn("Color system refresh failed (non-fatal):", e)
        );
    } catch (error) {
      console.error("Failed to save brand kit:", error);
      throw error;
    }

    setIsCreatingNewKit(false);
    setIsBrandKitModalOpen(false);
  };

  const handleDeleteBrandKit = async (kitId: string) => {
    const kitToDelete = userBrandKits.find((kit) => kit.id === kitId);
    if (!kitToDelete) return;

    if (
      window.confirm(
        `Are you sure you want to delete the brand kit "${kitToDelete.name}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteBrandKit(
          kitId,
          kitToDelete.logoPath,
          kitToDelete.bRollVideos
        );
        toast({
          title: "Brand Kit Deleted",
          description: `"${kitToDelete.name}" has been successfully deleted.`,
        });
        await fetchUserBrandKits();
        if (editingBrandKit && editingBrandKit.id === kitId) {
          handleCloseBrandKitModal();
        }
      } catch (error) {
        console.error("Failed to delete brand kit:", error);
        toast({
          variant: "destructive",
          title: "Delete Failed",
          description: "Could not delete the brand kit. Please try again.",
        });
      }
    }
  };

  const brandKitLimit = getBrandKitLimit();

  const atKitLimit =
    brandKitLimit !== null && userBrandKits.length >= brandKitLimit;

  return (
    <div className="min-h-full">
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Hero */}
        <header className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Brand kits
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Give Wavegen the business context, audience, and style behind your posts.
          </p>
        </header>

        <section>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Your kits
              {brandKitLimit !== null &&
                ` · ${userBrandKits.length} of ${brandKitLimit}`}
            </p>
            {atKitLimit ? (
              <button
                type="button"
                onClick={() => navigateToUpgrade({ reason: "brandkit-limit" })}
                className="text-xs font-medium text-brand transition-colors hover:text-brand/80"
              >
                Upgrade for more kits →
              </button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => handleOpenBrandKitModal()}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New kit
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="grid gap-5 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-64 animate-pulse overflow-hidden rounded-xl border bg-card"
                >
                  <div className="h-24 border-b bg-muted" />
                </div>
              ))}
            </div>
          ) : userBrandKits.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2">
              {userBrandKits.map((kit) => (
                <BrandKitCard
                  key={kit.id}
                  kit={kit}
                  onEdit={handleOpenBrandKitModal}
                  onDelete={handleDeleteBrandKit}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed py-16 text-center">
              <h3 className="text-lg font-semibold">
                Set up your first brand kit
              </h3>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                Import your colors, fonts, logo, and voice from your website —
                or build it by hand — and every post comes out on-brand.
              </p>
              <Button className="mt-5" onClick={() => handleOpenBrandKitModal()}>
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Kit
              </Button>
            </div>
          )}
        </section>
      </div>

      <BrandKitModal
        isOpen={isBrandKitModalOpen}
        onClose={handleCloseBrandKitModal}
        onSave={handleSaveBrandKit}
        initialData={editingBrandKit}
      />

    </div>
  );
}
